import type { ReactNode, CSSProperties } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import {
    ArrowUp,
    ArrowDown,
    ChevronDown,
    Filter,
    Search,
    X,
} from 'lucide-react';

import { listControlsInputColors } from '@/components/capability-list/list-controls-classes';

export interface SortOption {
    key: string;
    label: string;
}

export type ListControlsVariant = 'glass' | 'solid';

function SortButton({
    field,
    label,
    activeField,
    sortOrder,
    onSort,
}: {
    field: string;
    label: string;
    activeField: string;
    sortOrder: string;
    onSort: (field: string) => void;
}) {
    const isActive = activeField === field;
    return (
        <button
            type="button"
            onClick={() => onSort(field)}
            className={cn(
                'flex items-center gap-1 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                isActive
                    ? 'bg-[hsl(var(--list-controls-sort-active-bg))] text-[hsl(var(--list-controls-sort-active-fg))] shadow-sm'
                    : 'text-[hsl(var(--list-controls-muted-foreground))] hover:bg-[hsl(var(--list-controls-surface-hover))] hover:text-[hsl(var(--list-controls-foreground))]',
            )}
        >
            {label}
            {isActive &&
                (sortOrder === 'asc' ? (
                    <ArrowUp
                        size={12}
                        className="text-[hsl(var(--list-controls-sort-active-fg))]"
                    />
                ) : (
                    <ArrowDown
                        size={12}
                        className="text-[hsl(var(--list-controls-sort-active-fg))]"
                    />
                ))}
        </button>
    );
}

function TagsFilter({
    allTags,
    selectedTags,
    onToggle,
    onClearAll,
    onSelectAll,
    variant,
}: {
    allTags: string[];
    selectedTags: Set<string>;
    onToggle: (tag: string) => void;
    onClearAll: () => void;
    onSelectAll: () => void;
    variant: ListControlsVariant;
}) {
    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'flex min-w-[140px] cursor-pointer items-center justify-between rounded-md border py-2 pl-3 pr-2 text-sm font-medium',
                        'border-[hsl(var(--list-controls-border))] bg-[hsl(var(--list-controls-surface))] text-[hsl(var(--list-controls-foreground))]',
                        'hover:bg-[hsl(var(--list-controls-surface-hover))]',
                        variant === 'glass' &&
                            'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring/40',
                        variant === 'solid' &&
                            'focus-visible:border-ring focus-visible:outline-none focus-visible:ring-ring/50 focus-visible:ring-[3px]',
                    )}
                >
                    <span className="flex items-center gap-2">
                        <Filter
                            size={14}
                            className="text-[hsl(var(--list-controls-muted-foreground))]"
                        />
                        {selectedTags.size === 0
                            ? 'All Tags'
                            : `${selectedTags.size} tag${selectedTags.size !== 1 ? 's' : ''}`}
                    </span>
                    <ChevronDown
                        size={14}
                        className="text-[hsl(var(--list-controls-muted-foreground))]"
                    />
                </button>
            </PopoverTrigger>
            <PopoverContent className="w-[220px] p-2" align="end">
                <div className="mb-2 flex items-center justify-between border-b border-slate-100 pb-2">
                    <span className="text-xs font-medium text-slate-500">Filter by tags</span>
                    <div className="flex gap-1">
                        <button
                            type="button"
                            onClick={onSelectAll}
                            className="text-xs text-indigo-600 hover:underline"
                        >
                            All
                        </button>
                        <span className="text-slate-300">|</span>
                        <button
                            type="button"
                            onClick={onClearAll}
                            className="text-xs text-indigo-600 hover:underline"
                        >
                            None
                        </button>
                    </div>
                </div>
                <div className="max-h-[280px] space-y-1 overflow-y-auto">
                    {allTags.map((tag) => (
                        <label
                            key={tag}
                            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-slate-50"
                        >
                            <Checkbox
                                checked={selectedTags.has(tag)}
                                onCheckedChange={() => onToggle(tag)}
                            />
                            <span className="text-sm text-slate-700">{tag}</span>
                        </label>
                    ))}
                    {allTags.length === 0 && (
                        <p className="px-2 py-1.5 text-xs text-slate-400">No tags found</p>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}

export interface CapabilityListPanelProps<T> {
    items: T[];
    sortOptions?: SortOption[];
    sortField?: string;
    sortDirection?: string;
    onSort?: (field: string) => void;
    searchQuery: string;
    onSearchChange: (query: string) => void;
    searchPlaceholder?: string;
    allTags: string[];
    selectedTags: Set<string>;
    onToggleTag: (tag: string) => void;
    onClearAllTags: () => void;
    onSelectAllTags: () => void;
    showTagsFilter?: boolean;
    renderCard: (item: T, index: number) => ReactNode;
    emptyMessage?: string;
    /** When `glass`, empty state uses `--list-controls-empty-bg` (`app.css`). */
    emptyStateVariant?: 'default' | 'glass';
    extraToolbar?: ReactNode;
    /** Rendered before the search input (e.g. tab bars). */
    preSearchToolbar?: ReactNode;
    pagination?: ReactNode;
    isLoading?: boolean;
    loadingMessage?: string;
    gridClassName?: string;
    gridStyle?: CSSProperties;
    toolbarClassName?: string;
    contentClassName?: string;
    /** `glass` for hero headers; `solid` for drawers and white layouts. */
    listControlsVariant?: ListControlsVariant;
}

export function CapabilityListPanel<T>({
    items,
    sortOptions = [],
    sortField = '',
    sortDirection = 'asc',
    onSort,
    searchQuery,
    onSearchChange,
    searchPlaceholder = 'Search...',
    allTags,
    selectedTags,
    onToggleTag,
    onClearAllTags,
    onSelectAllTags,
    showTagsFilter = true,
    renderCard,
    emptyMessage = 'No items found.',
    emptyStateVariant = 'default',
    extraToolbar,
    preSearchToolbar,
    pagination,
    isLoading = false,
    loadingMessage = 'Loading...',
    gridClassName,
    gridStyle,
    toolbarClassName,
    contentClassName,
    listControlsVariant = 'glass',
}: CapabilityListPanelProps<T>) {
    return (
        <div className="flex min-h-0 flex-1 flex-col" style={{ height: '100%' }}>
            {/* Toolbar */}
            <div
                className={cn(
                    'list-controls-surface',
                    listControlsVariant === 'solid' && 'list-controls-surface--solid',
                    toolbarClassName ?? 'flex flex-col gap-4 p-6',
                )}
            >
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                    {sortOptions.length > 0 && onSort && (
                        <div className="flex items-center rounded-md border border-[hsl(var(--list-controls-border))] bg-[hsl(var(--list-controls-surface))] p-1">
                            {sortOptions.map((option) => (
                                <SortButton
                                    key={option.key}
                                    field={option.key}
                                    label={option.label}
                                    activeField={sortField}
                                    sortOrder={sortDirection}
                                    onSort={onSort}
                                />
                            ))}
                        </div>
                    )}

                    {preSearchToolbar}

                    <div className="relative flex-1">
                        <Search
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--list-controls-muted-foreground))]"
                            size={16}
                        />
                        <Input
                            placeholder={searchPlaceholder}
                            value={searchQuery}
                            onChange={(e) => onSearchChange(e.target.value)}
                            className={cn('w-full pl-9 pr-9', listControlsInputColors)}
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                onClick={() => onSearchChange('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-[hsl(var(--list-controls-muted-foreground))] hover:text-[hsl(var(--list-controls-foreground))]"
                            >
                                <X size={14} />
                            </button>
                        )}
                    </div>

                    {showTagsFilter && (
                        <TagsFilter
                            allTags={allTags}
                            selectedTags={selectedTags}
                            onToggle={onToggleTag}
                            onClearAll={onClearAllTags}
                            onSelectAll={onSelectAllTags}
                            variant={listControlsVariant}
                        />
                    )}

                    {extraToolbar}
                </div>
            </div>

            {/* Card Grid */}
            <div
                className={contentClassName ?? 'flex-1 px-6 pb-6'}
                style={{ minHeight: 0, overflow: 'auto' }}
            >
                {isLoading ? (
                    <div className="flex h-64 items-center justify-center rounded-lg border border-dashed border-slate-400 bg-slate-50">
                        <p className="text-sm text-slate-400">{loadingMessage}</p>
                    </div>
                ) : items.length > 0 ? (
                    <div
                        className={gridClassName ?? 'grid justify-start'}
                        style={
                            gridStyle ?? {
                                gridTemplateColumns:
                                    'repeat(auto-fill, minmax(265px, 322px))',
                                gap: 'clamp(10px, 2vw, 20px)',
                            }
                        }
                    >
                        {items.map((item, i) => renderCard(item, i))}
                    </div>
                ) : (
                    <div
                        className={cn(
                            'flex h-64 items-center justify-center rounded-lg border border-dashed border-slate-400',
                            emptyStateVariant === 'glass'
                                ? 'bg-[hsl(var(--list-controls-empty-bg))]'
                                : 'bg-slate-50',
                        )}
                    >
                        <div className="flex flex-col items-center gap-3">
                            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-[hsl(var(--list-controls-empty-search-chip-bg))]">
                                <Search size={24} className="text-blue-400" />
                            </div>
                            <p
                                className={cn(
                                    'text-sm',
                                    emptyStateVariant === 'glass'
                                        ? 'text-[hsl(var(--list-controls-foreground))]'
                                        : 'text-slate-400',
                                )}
                            >
                                {emptyMessage}
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {pagination}
        </div>
    );
}
